import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingLarge} from "@khanacademy/wonder-blocks-typography";
import {mediaQueries} from "@repo/shared-styles";
import {cssUrl, staticUrl} from "@repo/urls";

import {PAGE_WIDTH} from "./styles.ts";

type Props = {
    strings: {
        whatIsIt: string;
        whatIsItDescription: string;
        resourcesLink: string;
        ctaLearnMore: string;
        whyAlignDescription?: string;
        whyAlign?: string;
        learningAreas: Array<Array<string>>;
    };
    images: {
        learningAreas: Array<string>;
        map: string;
    };
};

const Section = ({
    title,
    description,
}: {
    title: string;
    description: string;
}) => {
    const descriptionParagraphs = description.split("\n");

    return (
        <React.Fragment>
            <HeadingLarge>{title}</HeadingLarge>
            <Strut size={spacing.large_24} />
            {descriptionParagraphs.map((desc, i) => (
                <Body
                    style={
                        i + 1 < descriptionParagraphs.length &&
                        styles.withBottomPadding
                    }
                    key={i}
                >
                    {desc}
                </Body>
            ))}
            <Strut size={spacing.large_24} />
        </React.Fragment>
    );
};

export default class Overview extends React.Component<Props> {
    render(): React.ReactNode {
        const {strings, images} = this.props;

        return (
            <View style={styles.overview}>
                <View style={styles.subsection1}>
                    <div
                        className={css(styles.imgContainer)}
                        style={{
                            backgroundImage: cssUrl(images.map),
                        }}
                    />
                    <View style={styles.right}>
                        <Section
                            title={strings.whatIsIt}
                            description={strings.whatIsItDescription}
                        />
                        <View style={styles.btnContainer}>
                            <Button
                                kind="secondary"
                                href={strings.resourcesLink}
                            >
                                {strings.ctaLearnMore}
                            </Button>
                        </View>
                        {strings.whyAlign && strings.whyAlignDescription && (
                            <React.Fragment>
                                <Strut size={spacing.xxLarge_48} />
                                <Section
                                    title={strings.whyAlign}
                                    description={strings.whyAlignDescription}
                                />
                            </React.Fragment>
                        )}
                    </View>
                </View>
                <View style={styles.topicSection}>
                    {strings.learningAreas.map((learningArea, i) => (
                        <View key={i} style={styles.topic}>
                            <View style={styles.topicHeading}>
                                {learningArea[0]}
                            </View>
                            <img
                                alt="Screenshot"
                                src={staticUrl(images.learningAreas[i])}
                                className={css(styles.topicImage)}
                            />
                            <Button kind="secondary" href={learningArea[1]}>
                                {learningArea[2]}
                            </Button>
                        </View>
                    ))}
                </View>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    overview: {
        marginTop: 66,
        marginBottom: 66,
        maxWidth: PAGE_WIDTH,
        alignSelf: "center",
        [mediaQueries.mdOrSmaller]: {
            marginLeft: 24,
            marginRight: 24,
            marginTop: 48,
            marginBottom: 24,
        },
    },
    subsection1: {
        flexDirection: "row",
        paddingBottom: 64,
        borderBottom: "1px solid rgba(33, 36, 44, 0.16)",
        [mediaQueries.mdOrSmaller]: {
            flexDirection: "row-reverse",
            paddingBottom: 48,
        },
        [mediaQueries.smOrSmaller]: {
            paddingTop: 48,
            flexDirection: "column",
        },
    },
    topicSection: {
        paddingTop: 64,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        [mediaQueries.smOrSmaller]: {
            paddingTop: 48,
            flexDirection: "column",
        },
    },
    topic: {
        height: 250,
        width: 251,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        [mediaQueries.smOrSmaller]: {
            marginTop: 48,
        },
    },
    topicHeading: {
        fontWeight: "bold",
        fontSize: 20,
    },
    topicImage: {
        alignItems: "center",
        width: 132,
        height: 124,
    },
    imgContainer: {
        width: 283,
        height: 205,
        marginRight: 48,
        borderRadius: 4,
        flexShrink: 0,
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        [mediaQueries.mdOrSmaller]: {
            marginRight: 0,
            marginLeft: 16,
            width: 168,
            height: 128,
            alignSelf: "center",
        },
        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },
    right: {},
    btnContainer: {
        flexDirection: "row",
    },
    withBottomPadding: {
        marginBottom: 24,
    },
});
